import { useEffect, useRef } from "react";
import { googleLogin } from "../services/login";
import { FcGoogle } from "react-icons/fc";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

/*global google*/
function GoogleSignInButton(props) {
  const { showError } = props;
  const hostname = window.location.hostname;
  const navigate = useNavigate();
  const clientRef = useRef(null);
  const handlerRef = useRef(null);

  const handleGoogleLogin = async (response) => {
    props.onStart?.();
    try {
      // Call the googleLogin function to get user data (like email)
      const res = await googleLogin(response.code, hostname);

      const email = res.data?.email;
      const username = res.data?.user?.username;
      if (email) {
        localStorage.setItem("email", email); // Save email to localStorage
      }
      if (username) {
        localStorage.setItem("username", username);
      }

      if (props.onSuccess) {
        props.onSuccess({
          email,
          fullName: res.auth?.full_name || "",
          needsOnboarding: !!res.auth?.needs_onboarding,
        });
        return;
      }

      // Continue with login success actions
      props.setVerified(true);
      props.loginMessage();
      const invitationToken = Cookies.get("invitation_token");
      if (invitationToken) navigate("/accept-invitation");
      else navigate("/rfp-dashboard");
    } catch (error) {
      console.error("Google login failed", error);

      const errorMessage = error?.response?.data?.non_field_errors?.[0];
      if (
        errorMessage === "User is already registered with this e-mail address."
      ) {
        showError(
          "Looks like you've already signed up using email and password. Please log in using your email instead of Google."
        );
      } else {
        showError("Google sign-in failed. Please try again.");
      }
    }
  };

  // Keep the ref pointing at the latest handler so the Google callback (created
  // once in the effect) never calls a stale closure.
  handlerRef.current = handleGoogleLogin;

  // Initialize the Google OAuth code client once the Identity script is ready.
  // Runs as an effect (not in render) and polls with a guard, so it never throws
  // "google is not defined" when the script is slow / blocked / not yet loaded.
  useEffect(() => {
    let cancelled = false;
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const googleReady = () =>
      typeof google !== "undefined" && google.accounts && google.accounts.oauth2;

    const init = () => {
      clientRef.current = google.accounts.oauth2.initCodeClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope:
          "https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email",
        callback: (response) => handlerRef.current(response),
      });
    };

    (async () => {
      for (let attempts = 0; attempts < 20 && !cancelled; attempts += 1) {
        if (googleReady()) {
          init();
          return;
        }
        await delay(1000);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = () => {
    if (clientRef.current) {
      clientRef.current.requestCode();
    } else if (showError) {
      showError("Google sign-in is still loading. Please try again in a moment.");
    }
  };

  if (props.render) return props.render({ onClick: handleClick });

  return (
    <button
      id="google-login-btn"
      onClick={handleClick}
      className="flex w-[355px] h-[44px] rounded-lg text-white font-semibold text-sm px-4
    bg-white hover:bg-[#FEF9F7] active:bg-[#FEF9F7] transition-colors duration-200"
    >
      <img
        src="/googleicon.svg"
        alt="Google icon"
        className="w-6 h-6 md:w-7 md:h-7 mt-[8px] "
      />

      <div
        className="googleText ml-4 mt-3"
        style={{
          color: "var(--Neutral-1, #3F3F46)",
          fontFamily: "Outfit, sans-serif",
          fontSize: "15px",
          fontWeight: 600,
          fontStyle: "normal",
          lineHeight: "normal",
        }}
      >
        <span>Continue with Google</span>
      </div>
    </button>
  );
}
export default GoogleSignInButton;
